import { Fragment, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import cls from 'classnames';

import { urlAPI } from '@/api/urlAPI';
import ArrowLeft from '@/assets/icons/icon-arrow-mid-left.svg?react';
import ArrowRight from '@/assets/icons/icon-arrow-mid-right.svg?react';
import OpenInNew from '@/assets/icons/icon-open-in-new.svg?react';
import PersonIcon from '@/assets/icons/icon-person.svg?react';
import ShapeNormalizedIcon from '@/assets/icons/icon-shape-normalized.svg?react';
import MyCollections from '@/assets/icons/icon-user-my-collections.svg?react';
import ContactsPopup from '@/components/ContactsPopup/ContactsPopup';
import Disclaimer from '@/components/Disclaimer/Disclaimer';
import ErrorPage from '@/components/ErrorPage/ErrorPage';
import Header from '@/components/Header/Header';
import Title from '@/components/Title/Title';
import { useStore } from '@/stores/StoreProvider';
import Button from '@/ui-kit/Button/Button';
import CirclesLoader from '@/ui-kit/CirclesLoader/CirclesLoader';

import BasicInfo from './BasicInfo/BasicInfo';
import Clarity from './Clarity/Clarity';
import CommonParameters from './CommonParameters/CommonParameters';
import styles from './DiamondPage.module.scss';
import OpticalPerformance from './OpticalPerformance/OpticalPerformance';
import PhotorealViewer from './PhotorealViewer/PhotorealViewer';
import Proportions from './Proportions/Proportions';
import Video from './Video/Video';

function DiamondPage() {
  const { fetchDiamond, diamondError, diamond, reports, prevDiamond, nextDiamond, contacts, dispose, openContactsPopup } = useStore('diamondsStore');
  const { id, collectionId } = useParams();

  useEffect(() => {
    fetchDiamond(id, collectionId);

    return () => {
      dispose();
    };
  }, [id, collectionId]);

  if (diamondError) {
    return <ErrorPage error={diamondError} />;
  }

  if (!diamond) {
    return <CirclesLoader />;
  }

  function renderControls() {
    if (!reports || reports.length === 0) {
      return null;
    }

    const prevUrl = prevDiamond();
    const nextUrl = nextDiamond();

    return (
      <div className={styles.controls}>
        <Link to={prevUrl} className={cls({ [styles.controlDisabled]: !prevUrl })}>
          <ArrowLeft width="25" height="25" />
        </Link>

        <Link to={urlAPI.new(collectionId)}>
          <MyCollections width="25" height="25" />
        </Link>

        <Link to={nextUrl} className={cls({ [styles.controlDisabled]: !nextUrl })}>
          <ArrowRight width="25" height="25" />
        </Link>
      </div>
    );
  }

  return (
    <div id="diamond-page" className={styles.root}>
      <Helmet>
        <title>{diamond.shortView}</title>
      </Helmet>

      <Header href={urlAPI.new(collectionId)} className={styles.header} renderControls={renderControls} />

      <Title isCutGrade={diamond.isCutGrade} isCut={diamond.isCut} />

      <Fragment>
        <div className={styles.heading}>
          <div className="container">
            {!diamond.isCut && (
              <div className={styles.description}>
                <div className={styles.descriptionRow}>
                  <div className={styles.descriptionRowLabel}>Product SKU</div>

                  <div className={styles.descriptionRowValue}>{diamond.sku}</div>
                </div>
              </div>
            )}

            {!diamond.isCut && diamond.labGrown && <div className={styles.labgrown}>LAB-GROWN diamond</div>}

            {diamond.isCut && diamond.cut?.title && (
              <h1 className={styles.subtitle}>
                <span>{diamond.cut.title}</span>

                <span>{diamond.carat ? `, ${diamond.carat}ct` : ''}</span>
              </h1>
            )}

            {!diamond.isCut && diamond.shortView && <h1 className={styles.subtitle}>{diamond.shortView}</h1>}

            {!diamond.isCut && (diamond.hasIntegralV2 || diamond.performance?.integral?.shape?.hr) && (
              <div className={styles.shapeMetric}>
                <div className={styles.shapeMetricLabel}>
                  {diamond.hasIntegralV2 ? 'Shape Normalized Performance v2 (Beta):' : 'Shape Normalized Performance:'}
                </div>

                <div className={styles.shapeMetricValue}>
                  <ShapeNormalizedIcon width="26" height="26" />

                  <span>&nbsp;{diamond.hasIntegralV2 ? diamond.shapeIntegralV2Hr : diamond.shapeIntegralHr}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {contacts && (
          <div className={styles.seller}>
            <div className="container">
              <Button onClick={openContactsPopup} startIcon={<PersonIcon />}>
                <span>Contact</span>

                {contacts.title && <span> {contacts.title}</span>}
              </Button>
            </div>
          </div>
        )}

        {diamond.render?.cbr && <PhotorealViewer />}

        <BasicInfo />

        <Proportions />

        {!diamond.isCut && <CommonParameters />}

        <OpticalPerformance />

        {diamond.isCutGrade && diamond.plottingDiagram && <Clarity />}

        {!diamond.isCut && <Video />}

        {!diamond.isCut && diamond.certificate && (
          <div className="container">
            <div className={styles.certificate}>
              <h2 className={styles.certificateTitle}>Certificate</h2>

              <div className={styles.link}>
                <a target="_blank" href={diamond.certificate.file ?? diamond.certificate.check}>
                  {diamond.certificate.laboratory && (
                    <Fragment>
                      <span>{diamond.certificate.laboratory}</span>

                      <span>&nbsp;</span>

                      <span>{diamond.certificate.number}</span>
                    </Fragment>
                  )}

                  <OpenInNew width="20" height="20" />
                </a>
              </div>
            </div>
          </div>
        )}
      </Fragment>

      <div className="container">
        <Disclaimer isCut={diamond.isCut} />
      </div>

      {contacts && <ContactsPopup contactsData={contacts} />}
    </div>
  );
}

export default observer(DiamondPage);
